import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { AlertMessage } from '@/components/ui/AlertMessage';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { Pagination } from '@/components/ui/Pagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { getUserStatusBadge, getAuthTypeBadge } from '@/utils/badgeUtils';
import { adminAPI } from '@/services/api';
import { User } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { PAGINATION } from '@/constants';
import { FILTER_VALUES } from '@/constants/users';
import { useSearchFilters, useCustomers } from '@/hooks/common';
import { 
  parseCustomerId, 
  stringifyCustomerId, 
  getRoleLabel,
  handleAPIError,
  getRoleOptions
} from '@/utils';
import { SearchFilters, SearchFieldConfig } from '@/components/common/SearchFilters';

interface AllUsersTabProps {
  onEditUser: (user: User) => void;
  onDeactivateUser: (user: User) => void | Promise<void>;
  onActivateUser?: (user: User) => void | Promise<void>;
  onDeleteUser?: (user: User) => void | Promise<void>;
  refreshKey?: number;
  // Admin 모드: 고정된 customerId 사용
  fixedCustomerId?: number | null;
  // Superadmin 모드: 고객사 필터 사용
  initialCustomerId?: number | null;
  approvingUserId?: number | null;
  // UI 옵션
  showCustomerFilter?: boolean;  // 고객사 필터 표시 여부
  showCustomerColumn?: boolean;  // 테이블에 고객사 컬럼 표시 여부
}

const AllUsersTab: React.FC<AllUsersTabProps> = ({
  onEditUser,
  onDeactivateUser,
  onActivateUser,
  onDeleteUser,
  refreshKey,
  fixedCustomerId,
  initialCustomerId,
  approvingUserId: _approvingUserId,
  showCustomerFilter = false,
  showCustomerColumn = false,
}) => {
  // Admin 모드에서는 currentUser를 사용하지 않지만, useAuth는 유지 (향후 확장 가능)
  useAuth();
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = PAGINATION.USER_MANAGEMENT_PAGE_SIZE;
  
  // 고객사 목록 (Superadmin 모드에서만 사용)
  const { customers } = useCustomers();
  
  // Admin 모드: 고정된 customerId 사용
  const isAdminMode = fixedCustomerId !== undefined;
  const effectiveCustomerId = isAdminMode ? fixedCustomerId : null;
  
  // Superadmin 모드: URL과 동기화되는 필터 상태
  const {
    selectedCustomerIdStr: [selectedCustomerIdStr, setSelectedCustomerIdStr],
    selectedStatus: [selectedStatus, setSelectedStatus],
    selectedRole: [selectedRole, setSelectedRole],
    includeDeleted: [includeDeleted, setIncludeDeleted],
    searchQuery: [searchQuery, setSearchQuery],
  } = useSearchFilters('allUsers', {
    selectedCustomerIdStr: showCustomerFilter ? 'all' : '',
    selectedStatus: '',
    selectedRole: '',
    includeDeleted: false as boolean,
    searchQuery: '',
  });
  
  // Superadmin 모드: 문자열을 number | null | undefined로 변환
  const selectedCustomerId = showCustomerFilter ? parseCustomerId(selectedCustomerIdStr as string) : null;
  const setSelectedCustomerId = (value: number | null | undefined) => {
    if (showCustomerFilter) {
      setSelectedCustomerIdStr(stringifyCustomerId(value));
    }
  };

  // initialCustomerId가 변경되면 selectedCustomerId 업데이트 (Superadmin 모드)
  useEffect(() => {
    if (showCustomerFilter && initialCustomerId !== undefined && initialCustomerId !== selectedCustomerId) {
      setSelectedCustomerId(initialCustomerId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCustomerId, showCustomerFilter]);

  const fetchAllUsers = useCallback(async () => {
    // Admin 모드: customerId가 없으면 조회하지 않음
    if (isAdminMode && !effectiveCustomerId) {
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      const skip = (page - 1) * pageSize;
      
      if (isAdminMode) {
        // Admin 모드: getCustomerUsers API 사용
        const response = await adminAPI.getCustomerUsers(
          effectiveCustomerId!,
          skip,
          pageSize,
          selectedStatus || undefined,
          searchQuery || undefined,
          includeDeleted,
          selectedRole || undefined
        );
        setAllUsers(response.users || []);
        setTotal(response.total || 0);
      } else {
        // Superadmin 모드: getAllUsers API 사용
        const customerIdForAPI = selectedCustomerId === null ? undefined : (selectedCustomerId || undefined);
        const response = await adminAPI.getAllUsers(
          skip,
          pageSize,
          customerIdForAPI,
          selectedStatus || undefined,
          searchQuery || undefined,
          includeDeleted,
          selectedRole || undefined
        );
        setAllUsers(response.users || []);
        setTotal(response.total || 0);
      }
    } catch (error: any) {
      handleAPIError(error, '사용자 목록을 불러오는데 실패했습니다.', setError);
    } finally {
      setLoading(false);
    }
  }, [
    isAdminMode,
    effectiveCustomerId,
    page,
    pageSize,
    selectedCustomerId,
    selectedStatus,
    searchQuery,
    includeDeleted,
    selectedRole,
  ]);

  // 필터 값 객체 (SearchFilters 컴포넌트용)
  const filterValues = useMemo(() => {
    const base = {
      status: selectedStatus || FILTER_VALUES.ALL,
      role: selectedRole || FILTER_VALUES.ALL,
      search: searchQuery,
    };
    
    if (showCustomerFilter) {
      return {
        ...base,
        customer: selectedCustomerId === null ? 'none' : (selectedCustomerId === undefined ? 'all' : String(selectedCustomerId)),
      };
    }
    
    return base;
  }, [selectedStatus, selectedRole, searchQuery, selectedCustomerId, showCustomerFilter]);

  // 필터 필드 설정
  const filterFields: SearchFieldConfig[] = useMemo(() => {
    const fields: SearchFieldConfig[] = [];
    
    // 고객사 필터 (Superadmin 모드에서만)
    if (showCustomerFilter) {
      fields.push({
        key: 'customer',
        type: 'select',
        label: '고객사 필터',
        colSpan: 2,
        options: [
          { value: 'none', label: '(미입력)' },
          { value: 'all', label: '전체', className: 'font-bold' },
          ...customers.map(customer => ({
            value: String(customer.id),
            label: customer.customer_name,
          })),
        ],
        onChange: (value) => {
          if (value === 'none') {
            setSelectedCustomerId(null);
          } else if (value === 'all') {
            setSelectedCustomerId(undefined);
          } else {
            setSelectedCustomerId(parseInt(value, 10));
          }
        },
      });
    }
    
    // 상태 필터
    fields.push({
      key: 'status',
      type: 'select',
      label: '상태 필터',
      colSpan: 2,
      options: [
        { value: FILTER_VALUES.ALL, label: '전체' },
        { value: FILTER_VALUES.ACTIVE, label: '활성화' },
        { value: FILTER_VALUES.DEACTIVATED, label: '비활성화' },
      ],
      onChange: (value) => {
        setSelectedStatus(value === FILTER_VALUES.ALL ? '' : value);
      },
    });
    
    // 역할 필터
    fields.push({
      key: 'role',
      type: 'select',
      label: '역할 필터',
      colSpan: 2,
      options: getRoleOptions().map(opt => ({ value: opt.value, label: opt.label })),
      onChange: (value) => {
        setSelectedRole(value === FILTER_VALUES.ALL ? '' : value);
      },
    });
    
    // 검색 필드
    fields.push({
      key: 'search',
      type: 'input',
      label: '이메일 또는 이름 검색',
      placeholder: '이메일 또는 이름으로 검색...',
      colSpan: showCustomerFilter ? 4 : 6,
      onChange: (value) => {
        setSearchQuery(value);
      },
      onEnter: () => {
        setPage(1);
        fetchAllUsers();
      },
    });
    
    return fields;
  }, [showCustomerFilter, customers, setSelectedCustomerId, setSelectedStatus, setSelectedRole, setSearchQuery, fetchAllUsers]);

  // 필터 변경 핸들러
  const handleFilterChange = (key: string, value: any) => {
    if (key === 'customer' && showCustomerFilter) {
      if (value === 'none') {
        setSelectedCustomerId(null);
      } else if (value === 'all') {
        setSelectedCustomerId(undefined);
      } else {
        setSelectedCustomerId(parseInt(value, 10));
      }
    } else if (key === 'status') {
      setSelectedStatus(value === 'all' ? '' : value);
    } else if (key === 'role') {
      setSelectedRole(value === 'all' ? '' : value);
    } else if (key === 'search') {
      setSearchQuery(value);
    }
  };

  // 검색 핸들러
  const handleSearch = useCallback(() => {
    setPage(1);
    fetchAllUsers();
  }, [fetchAllUsers]);

  // 초기화 핸들러
  const handleReset = useCallback(() => {
    if (showCustomerFilter) {
      setSelectedCustomerId(undefined);
    }
    setSelectedStatus('');
    setSelectedRole('');
    setSearchQuery('');
    setIncludeDeleted(false);
    setPage(1);
    setTimeout(() => fetchAllUsers(), 0);
  }, [showCustomerFilter, setSelectedCustomerId, setSelectedStatus, setSelectedRole, setSearchQuery, setIncludeDeleted, fetchAllUsers]);

  // 초기 로드 및 페이지 변경, refreshKey 변경 시에만 검색
  useEffect(() => {
    if (isAdminMode && !effectiveCustomerId) {
      return;
    }
    fetchAllUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminMode ? effectiveCustomerId : selectedCustomerId, page, refreshKey]);

  const handleDeactivateUserWrapper = async (user: User) => {
    await onDeactivateUser(user);
    fetchAllUsers();
  };

  const handleActivateUserWrapper = async (user: User) => {
    if (onActivateUser) {
      await onActivateUser(user);
      fetchAllUsers();
    } else {
      // fallback: 직접 활성화 (하위 호환성)
      try {
        setLoading(true);
        await adminAPI.updateUser(user.id, { status: FILTER_VALUES.ACTIVE });
        fetchAllUsers();
      } catch (error: any) {
        handleAPIError(error, '사용자 활성화에 실패했습니다.', setError);
      } finally {
        setLoading(false);
      }
    }
  };

  // Admin 모드: customerId가 없으면 메시지 표시
  if (isAdminMode && !effectiveCustomerId) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-gray-500">
            연결된 고객사가 없습니다.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>전체 사용자</CardTitle>
          <CardDescription>
            {showCustomerColumn
              ? '모든 고객사의 사용자를 검색할 수 있습니다. 이메일로 검색하거나 고객사/상태로 필터링할 수 있습니다.'
              : '고객사 내 모든 사용자를 조회하고 관리합니다.'}
          </CardDescription>
        </div>
        <div className="flex items-center space-x-2">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(includeDeleted)}
              onChange={(e) => {
                setIncludeDeleted(e.target.checked);
              }}
              className="rounded"
            />
            <span className="text-sm text-gray-700">삭제된 사용자 포함</span>
          </label>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* 검색 및 필터 - 공통 컴포넌트 사용 */}
        <SearchFilters
          fields={filterFields}
          values={filterValues}
          onChange={handleFilterChange}
          onSearch={handleSearch}
          onReset={handleReset}
          loading={loading}
          showSearchButton={true}
        />

        {error && (
          <AlertMessage 
            type="error" 
            message={error} 
            onClose={() => {}} 
          />
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" message="사용자를 불러오는 중..." />
          </div>
        ) : (() => {
          if (allUsers.length === 0) {
            return (
              <EmptyState
                message={
                  searchQuery || (showCustomerFilter && selectedCustomerId) || selectedStatus || selectedRole
                    ? '검색 결과가 없습니다.'
                    : '사용자가 없습니다.'
                }
              />
            );
          }

          return (
            <>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {showCustomerColumn && <TableHead>고객사</TableHead>}
                      <TableHead>상태</TableHead>
                      <TableHead>이메일</TableHead>
                      <TableHead>이름</TableHead>
                      <TableHead>역할</TableHead>
                      <TableHead>로그인 유형</TableHead>
                      <TableHead className="text-right">작업</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {allUsers.map((user) => {
                      const isDeleted = user.status === 'deleted';
                      return (
                        <TableRow 
                          key={user.id} 
                          className={isDeleted ? 'opacity-50 bg-gray-100' : ''}
                        >
                          {showCustomerColumn && (
                            <TableCell className={`text-sm ${isDeleted ? 'text-gray-500' : 'text-gray-500'}`}>
                              {user.customer?.customer_name || '(미할당)'}
                            </TableCell>
                          )}
                          <TableCell>
                            {getUserStatusBadge(user.status)}
                          </TableCell>
                          <TableCell className={`text-sm font-medium ${isDeleted ? 'text-gray-500' : 'text-gray-900'}`}>
                            {user.email}
                          </TableCell>
                          <TableCell className={`text-sm ${isDeleted ? 'text-gray-500' : 'text-gray-500'}`}>
                            {user.name || '-'}
                          </TableCell>
                          <TableCell className={`text-sm ${isDeleted ? 'text-gray-500' : 'text-gray-500'}`}>
                            {getRoleLabel(user.role)}
                          </TableCell>
                          <TableCell>
                            {getAuthTypeBadge(user.auth_type)}
                          </TableCell>
                          <TableCell className="text-right">
                            {!isDeleted && (
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => onEditUser(user)}
                                >
                                  수정
                                </Button>
                                {user.status === FILTER_VALUES.ACTIVE ? (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDeactivateUserWrapper(user)}
                                  >
                                    비활성화
                                  </Button>
                                ) : user.status === 'deactivated' && (
                                  <>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleActivateUserWrapper(user)}
                                    >
                                      활성화
                                    </Button>
                                    {onDeleteUser && (
                                      <Button
                                        variant="destructive"
                                        size="sm"
                                        onClick={() => onDeleteUser(user)}
                                      >
                                        삭제
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <Pagination
                currentPage={page}
                totalPages={Math.ceil(total / pageSize)}
                totalItems={total}
                itemsPerPage={pageSize}
                onPageChange={(newPage) => setPage(newPage)}
                showItemsPerPage={false}
                showTotal={true}
                className="mt-4"
              />
            </>
          );
        })()}
      </CardContent>
    </Card>
  );
};

export default AllUsersTab;
